<script setup>
import OnboardingFeatureCard from './OnboardingFeatureCard.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';

const getters = useStoreGetters();
const { t } = useI18n();
const globalConfig = computed(() => getters['globalConfig/get'].value);
const currentUser = computed(() => getters.getCurrentUser.value);
const onboardingStepCount = 4;

const greetingMessage = computed(() => {
  const hours = new Date().getHours();
  const translationParams = {
    name: currentUser.value.name,
    installationName: globalConfig.value.installationName,
  };
  if (hours < 12) {
    return t('ONBOARDING.GREETING_MORNING', translationParams);
  }
  if (hours < 18) {
    return t('ONBOARDING.GREETING_AFTERNOON', translationParams);
  }
  return t('ONBOARDING.GREETING_EVENING', translationParams);
});
</script>

<template>
  <div
    class="mx-auto flex min-h-full w-full max-w-6xl flex-col gap-5 overflow-auto p-5 font-inter sm:p-8"
  >
    <div
      class="flex flex-col gap-5 rounded-2xl border border-n-weak bg-n-solid-1 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
    >
      <div class="flex min-w-0 items-start gap-4">
        <span
          class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-sparkles" class="size-5" />
        </span>
        <div class="min-w-0">
          <p
            class="mb-1 text-xl font-semibold tracking-[0.2px] text-n-slate-12 font-interDisplay"
          >
            {{ greetingMessage }}
          </p>
          <p class="mb-0 max-w-2xl text-sm leading-6 text-n-slate-11">
            {{
              $t('ONBOARDING.DESCRIPTION', {
                installationName: globalConfig.installationName,
              })
            }}
          </p>
        </div>
      </div>
      <div
        class="flex shrink-0 items-center gap-2 rounded-xl border border-n-weak bg-n-slate-2 px-3 py-2 text-xs font-medium text-n-slate-11"
      >
        <Icon icon="i-lucide-list-checks" class="size-4 text-n-brand" />
        <span>{{ onboardingStepCount }}</span>
      </div>
    </div>
    <div class="grid gap-4 md:grid-cols-2">
      <OnboardingFeatureCard
        step="01"
        image-src="/dashboard/images/onboarding/omnichannel-inbox.png"
        image-alt="Omnichannel"
        to="settings_inbox_new"
        :title="$t('ONBOARDING.ALL_CONVERSATION.TITLE')"
        :description="$t('ONBOARDING.ALL_CONVERSATION.DESCRIPTION')"
        :link-text="$t('ONBOARDING.ALL_CONVERSATION.NEW_LINK')"
      />
      <OnboardingFeatureCard
        step="02"
        image-src="/dashboard/images/onboarding/teams.png"
        image-alt="Teams"
        to="settings_teams_new"
        :title="$t('ONBOARDING.TEAM_MEMBERS.TITLE')"
        :description="$t('ONBOARDING.TEAM_MEMBERS.DESCRIPTION')"
        :link-text="$t('ONBOARDING.TEAM_MEMBERS.NEW_LINK')"
      />
      <OnboardingFeatureCard
        step="03"
        image-src="/dashboard/images/onboarding/canned-responses.png"
        image-alt="Canned responses"
        to="canned_list"
        :title="$t('ONBOARDING.CANNED_RESPONSES.TITLE')"
        :description="$t('ONBOARDING.CANNED_RESPONSES.DESCRIPTION')"
        :link-text="$t('ONBOARDING.CANNED_RESPONSES.NEW_LINK')"
      />
      <OnboardingFeatureCard
        step="04"
        image-src="/dashboard/images/onboarding/labels.png"
        image-alt="Labels"
        to="labels_list"
        :title="$t('ONBOARDING.LABELS.TITLE')"
        :description="$t('ONBOARDING.LABELS.DESCRIPTION')"
        :link-text="$t('ONBOARDING.LABELS.NEW_LINK')"
      />
    </div>
  </div>
</template>
